import 'consumerweb-shim';
import 'adsinterceptor';
import PropTypes from 'prop-types';
import React from 'react';
import { Footer } from 'marketing-components';
import ReactDOM from 'react-dom';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import { Row, Col } from 'react-bootstrap';
import {
  BodyText,
  Button,
  CaptionText,
  HeadingText,
  grey600,
  BrowserIcon,
  Link,
  v2blue400,
  v2neutral100,
  v2neutral300,
} from '@paypalcorp/pp-react';
import MediaQuery from 'react-responsive';
import * as analytics from '../lib/analytics';

import SimpleHeader from '../components/SimpleHeader';
import CharityLogo from '../components/charityLogo';
import DonateBox from '../components/donateBox';
import Benefits from '../components/benefits/benefits';
import SocialShare from '../components/SocialShare';
import CheckoutError from '../components/checkoutError';
import CobrandedHeader from '../components/cobranded-header';

import content from '../lib/i18n';
import styles from './fundraiser-charity.less';
import { getType, isPPGF } from '../lib/charity';
import DonationTerms from '../components/donation-terms';
import glamorous from 'glamorous';
import {
  CAMPAIGN_PAGE_GROUP,
  CAMPAIGN_PROFILE_PAGE,
} from '../lib/fptiTrackingVariables';
import SheetTrigger from '../components/SheetTrigger';
const i18n = content('campaignProfilePage');
import { ThemeProvider } from '@paypalcorp/pp-react-theme';
import { DataContext } from '../lib/context';
const HeroStyle = {
  backgroundImage: 'linear-gradient(279deg, #1546a0, #0070ba)',
};

/*
  This page is the charity profile page for charities that are
  part of a campaign. The difference between this page, and the
  hub-npo.js page is that a user who sets up a campaign/fundraiser
  can customize features, content, and the view, of the campaign
  which are propogated to this charity page.

  TODO: Consolidate this and the hub-npo.js page to let us use
  only one component for this page.
*/

const DonationTermsStyle = glamorous.div({
  padding: '10px 30px 10px 30px',
  borderTop: '1px solid #e2e8ec',
});

const LegalTerms = glamorous(CaptionText)({
  '& a': {
    color: v2blue400,
  },
});

const SocialShareContainer = glamorous.div({
  '@media(max-width: 500px)': {
    margin: '16px',
    borderColor: v2neutral300,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '48px',
  },
  '@media(min-width: 500px)': {
    margin: '4px',
    borderColor: v2neutral300,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '48px',
  },
});

class Page extends React.Component {
  static propTypes = {
    csrf: PropTypes.string,
    description: PropTypes.string,
    website_url: PropTypes.string,
    mission_area: PropTypes.array,
    resources: PropTypes.object,
    states: PropTypes.object,
    address: PropTypes.object,
  };

  static childContextTypes = {
    csrf: PropTypes.string,
  };

  constructor(props, context) {
    super(props, context);
    const initialState = {
      fptiInfo: {
        pgrp: CAMPAIGN_PAGE_GROUP,
        page: CAMPAIGN_PROFILE_PAGE,
      },
    };
    this.state = initialState;
  }

  parseFPTIInfo = (fptiData) => {
    var nameValues = [];
    var pairs = decodeURIComponent(fptiData).split('&');
    var i;
    for (i = 0; i < pairs.length; i++) {
      var parts = pairs[i].split('=');
      nameValues[parts[0]] = parts[1];
    }
    return nameValues;
  };

  getChildContext() {
    return {
      csrf: this.props.csrf,
    };
  }

  handleAnalyticsCategory = (category) => {
    analytics.trackLinkWithData(
      'CategoryClicked',
      this.state.fptiInfo.pgrp,
      this.state.fptiInfo.page,
      { category },
      'link'
    );
  };

  handleAnalyticsWebLink = () => {
    analytics.trackLink(
      'WebsiteLink',
      this.state.fptiInfo.pgrp,
      this.state.fptiInfo.page,
      'link'
    );
  };

  renderHeader = () => {
    const cobranded = get(
      this.props.campaign,
      'templateData.campaignBranding.campaignBrand',
      ''
    );
    const logo = get(this.props.campaign, 'templateData.lpContents.logoImage');
    if (cobranded.toUpperCase() === 'COBRANDED' && logo) {
      return (
        <CobrandedHeader cobrandedLogo={logo} cdnHost={this.props.cdnHost} />
      );
    } else {
      return (
        <SimpleHeader
          i18n={i18n}
          hasBorder={false}
          hasHomeLink={true}
          homeLink={this.props.resources.homeLink}
          cdnHost={this.props.cdnHost}
          domain={this.props.domain}
        />
      );
    }
  };

  renderAbout = () => {
    const npid = this.props.nonprofit_id;
    const charityContent = this.props.charityContent;

    let categories;
    if (Array.isArray(this.props.cause_area)) {
      const causeAreaIDs = uniq(this.props.cause_area.map((ca) => ca.id));
      categories = causeAreaIDs.map((id) => ({
        value: String(id),
        label: i18n(`label.category-${id}`),
      }));
    } else {
      categories = [];
    }

    let link;
    if (this.props.website_url) {
      link = (
        <Link
          onClick={this.handleAnalyticsWebLink}
          target={'_blank'}
          href={this.props.website_url}
          leftIcon={<BrowserIcon size='sm' />}
        >
          {i18n('label.view-website')}
        </Link>
      );
    }

    let ein;
    if (this.props.ein) {
      ein = (
        <CaptionText as='p' size='md' style={{ color: grey600 }}>
          {`${i18n(charityContent.einLabel)}`}&nbsp;
          <span>{this.props.ein}</span>
        </CaptionText>
      );
    }

    let source;
    const charityType = get(this.props.charity, 'charity_type');
    if (charityContent.source && isPPGF(charityType)) {
      source = (
        <BodyText as='p' size='md' style={{ color: grey600 }}>
          {`${i18n(charityContent.sourceLabel)}`}&nbsp;
          {i18n(charityContent.source)}
        </BodyText>
      );
    }

    const address = this.props.address;
    const cityState = (
      <CaptionText
        as='p'
        size='md'
        style={{ color: grey600, paddingBottom: '12px' }}
      >
        {[address.city, address.state].filter((x) => !!x).join(', ')}
      </CaptionText>
    );

    const about = (
      <div className={'panel'}>
        <div>
          <HeadingText as='h1' size='md' className={styles['npo-charityTitle']}>
            {i18n('label.about-this-charity')}
          </HeadingText>
          <BodyText as='p' size='md'>
            {this.props.description}
          </BodyText>
          <div style={{ paddingTop: '30px' }}>
            {categories.map((category, categoryIndex) => {
              if (categoryIndex === categories.length - 1) {
                return (
                  <Link
                    onClick={this.handleAnalyticsCategory.bind(
                      this,
                      category.value
                    )}
                    key={`np-${npid}-${categoryIndex}`}
                    href={`${this.props.resources.searchLink}?category=${category.value}`}
                  >
                    <BodyText
                      size='md'
                      regular
                      className={styles.categoryDisplay}
                    >
                      {category.label}
                    </BodyText>
                  </Link>
                );
              } else {
                return (
                  <span>
                    <Link
                      onClick={this.handleAnalyticsCategory.bind(
                        this,
                        category.value
                      )}
                      key={`np-${npid}-${categoryIndex}`}
                      href={`${this.props.resources.searchLink}?category=${category.value}`}
                    >
                      <BodyText
                        size='md'
                        regular
                        className={styles.categoryDisplay}
                      >
                        {category.label}
                      </BodyText>
                    </Link>
                    ,{' '}
                  </span>
                );
              }
            })}
          </div>
          <div>
            {ein}
            {cityState}
            {link}
            {source}
          </div>
        </div>
      </div>
    );
    return about;
  };

  renderBenefits = () => {
    const charityType = get(this.props.charity, 'charity_type');
    if (isPPGF(charityType)) {
      let percentage;
      if (this.props.campaign.countryCode === 'GB') {
        percentage = '100%';
      } else {
        percentage = '101%';
      }

      return (
        <div className={'panel'} style={{ backgroundColor: v2neutral100 }}>
          <Benefits
            i18n={i18n}
            layout={'vertical'}
            percentToCharity={percentage}
            cdnHost={this.props.cdnHost}
          />
        </div>
      );
    }
  };

  renderSocialShare = () => {
    let social;
    const socialShareNeeded = get(
      this.props,
      'campaign.templateData.socialShareNeeded',
      'yes'
    );
    if (socialShareNeeded === 'yes') {
      social = (
        <SocialShareContainer>
          <SocialShare
            url={window.location.href}
            campaignInfo={this.props.campaign}
            fptiInfo={this.state.fptiInfo}
            style={{ marginTop: '16px' }}
          />
          <div className={'vx_small-text'} style={{ textAlign: 'center' }}>
            <BodyText as='p' size='md' strong>
              {i18n('label.spread-the-word')}
            </BodyText>
            <CaptionText size='md' style={{ marginTop: '15px' }}>
              {i18n('label.share-message')}
            </CaptionText>
          </div>
        </SocialShareContainer>
      );
      return social;
    }
  };

  renderDonationTerms = () => {
    const type = getType(this.props.charity);
    const charityContent = this.props.charityContent;

    return (
      <DonationTermsStyle>
        <DonationTerms
          i18n={i18n}
          type={type}
          charity={charity}
          donationTerms={charityContent.donationTerms}
        />
      </DonationTermsStyle>
    );
  };

  renderDonate = () => {
    const formUrl =
      this.props.coBrand + 'charity/' + this.props.nonprofit_id + '/xo/create';

    const donate = (
      <div className={`panel ${styles.donateBoxPanel}`}>
        <DonateBox
          i18n={i18n}
          {...this.props}
          formUrl={formUrl}
          charity={this.props.charity}
          enable={this.props.campaign.state === 'ACTIVE'}
          fptiInfo={this.state.fptiInfo}
          label_alltocharity={i18n('label.all-to-charity')}
          cdnHost={this.props.cdnHost}
        />
        {this.renderDonationTerms()}
      </div>
    );
    return donate;
  };

  renderGiftsDoMore = () => {
    const charityType = get(this.props.charity, 'charity_type');
    const charityContent = this.props.charityContent;

    return (
      <div>
        <div className={'npo-gifts-do-more'}>
          <LegalTerms
            className={'recieptInfo'}
            dangerouslySetInnerHTML={{
              __html: isPPGF(charityType)
                ? i18n(charityContent.legalDisclaimer)
                : i18n('label.legal-footer-default'),
            }}
            as='p'
            size='sm'
          />
        </div>
      </div>
    );
  };

  renderFooter = () => {
    const { content: footerContent, localeCountry } = JSON.parse(
      document.getElementById('data-footer').innerHTML
    );
    return (
      <Footer
        content={footerContent}
        localeCountry={localeCountry}
        flagHref={`https://www.${
          this.props.domain
        }/${localeCountry.toLowerCase()}/webapps/mpp/country-worldwide`}
      />
    );
  };

  renderError = () => {
    if (this.props.donationInfo && this.props.donationInfo.donated === 0) {
      return (
        <CheckoutError
          i18n={i18n}
          fptiInfo={this.state.fptiInfo}
          cdnHost={this.props.cdnHost}
        />
      );
    }
  };

  render() {
    const about = this.renderAbout();
    const benefits = this.renderBenefits();
    const social = this.renderSocialShare();
    const donate = this.renderDonate();
    const giftsDoMore = this.renderGiftsDoMore();

    return (
      <div>
        <ThemeProvider theme='v2'>
          {this.renderHeader()}
          <div className={'cause-viewport'}>
            <div>
              <div className={'cause-charity-hero'} style={HeroStyle} />
              <div style={{ maxWidth: '1024px', margin: 'auto' }}>
                <MediaQuery minWidth={901}>
                  <CharityLogo {...this.props} i18n={i18n} />
                  <Row>
                    <Col xs={7} md={7} lg={7}>
                      {about}
                    </Col>
                    <Col xs={5} md={5} lg={5}>
                      {donate}
                      {social}
                    </Col>
                  </Row>
                </MediaQuery>
                <MediaQuery minWidth={601} maxWidth={900}>
                  <CharityLogo {...this.props} i18n={i18n} />
                  <Row>
                    <Col xs={6} md={6} lg={6}>
                      {about}
                    </Col>
                    <Col xs={6} md={6} lg={6}>
                      {donate}
                      {social}
                    </Col>
                  </Row>
                </MediaQuery>
                <MediaQuery maxWidth={600}>
                  <CharityLogo {...this.props} i18n={i18n} />
                  {about}
                  <SheetTrigger
                    TriggerComponent={
                      <Button
                        className={styles['sheet-trigger-button']}
                      >{`${i18n('label.donate-button')}`}</Button>
                    }
                    sheetProps={{
                      title: `${i18n('label.donate-button')}`,
                      divider: true,
                    }}
                  >
                    {donate}
                  </SheetTrigger>
                  {social}
                </MediaQuery>
              </div>
              {benefits}
              <div>{giftsDoMore}</div>
              {this.renderError()}
              {this.props.mppFooter && this.renderFooter()}
            </div>
          </div>
        </ThemeProvider>
      </div>
    );
  }
}

const campaign = JSON.parse(
  document.getElementById('campaign-props').innerHTML
);
const data = JSON.parse(document.getElementById('data-props').innerHTML);
const charity = JSON.parse(document.getElementById('charity-props').innerHTML);
const donationInfo = JSON.parse(
  document.getElementById('donation-info-props').innerHTML || '{}'
);
const states = JSON.parse(document.getElementById('states-props').innerHTML);
const amounts = JSON.parse(document.getElementById('amounts-props').innerHTML);
const fptiInfo = JSON.parse(
  document.getElementById('data-fpti-info').innerHTML
);
const csrf = document.getElementById('data-csrf').innerHTML;
const coBrand = document.getElementById('data-co-brand').innerHTML;
const hubUrl = `${coBrand}hub`;
const resources = JSON.parse(
  document.getElementById('data-resources').innerHTML
);
const mppFooter = data.mppFooter;
const cdnHost = data.cdnHost;
const domain = data.domain;
ReactDOM.render(
  <DataContext.Provider value={data}>
    <Page
      {...charity}
      charity={charity}
      id={'nemo_npo'}
      campaign={campaign}
      donationInfo={donationInfo}
      states={states}
      csrf={csrf}
      fptiInfo={fptiInfo}
      coBrand={coBrand}
      amounts={amounts}
      hubUrl={hubUrl}
      resources={resources}
      mppFooter={mppFooter}
      cdnHost={cdnHost}
      domain={domain}
    />
  </DataContext.Provider>,
  document.getElementById('page')
);
